/**
 * The AFF simulator on the Live Aircraft layer card (#866).
 *
 *   AffSimulatorBanner    everyone with aff.read: says when aircraft on the
 *                         live layer are simulated
 *   AffSimulatorControls  aff.simulate: Start, Stop and Clear
 *
 * EasyMap asks, the collector acts: a button only records a request, which the
 * AFF collector carries out on its next run.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { dateTimeInNwt } from '../../../utils/nwtDate';
import { fetchAffSimStatus, requestAffSim, affSimEnvLabel, type AffSimAction, type AffSimStatus } from '../../../services/affSimService';
import { useConfirm } from '../../../components/common/AppConfirm';

export const SIM_STATUS_POLL_MS = 60_000;
/** While a request waits for the collector, so the card and map follow it within seconds. */
export const SIM_PENDING_POLL_MS = 5_000;

/**
 * The simulator status, polled while `enabled`. Null until first loaded; a
 * failed read keeps the last status, so the banner does not vanish while
 * simulated aircraft are still on the map.
 */
export function useAffSimStatus(enabled: boolean): { status: AffSimStatus | null; refresh: () => Promise<void> } {
  const [status, setStatus] = useState<AffSimStatus | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await fetchAffSimStatus());
    } catch {
      // Keep what was last read.
    }
  }, []);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);

  const pending = Boolean(status?.pending);
  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(refresh, pending ? SIM_PENDING_POLL_MS : SIM_STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, pending, refresh]);

  return { status, refresh };
}

export const AffSimulatorBanner: React.FC<{ status: AffSimStatus | null }> = ({ status }) => {
  if (!status || (!status.startedAt && status.simPositions === 0)) return null;
  const env = affSimEnvLabel(status.environment);
  return (
    <div className="layer-card-sim-banner" role="note" aria-label="AFF simulator">
      {status.startedAt
        ? `Simulation running on ${env} since ${dateTimeInNwt(status.startedAt)} - aircraft marked SIM are not real`
        : `Simulation ended on ${env} - the aircraft marked SIM are not real and stay until cleared`}
    </div>
  );
};

const LABEL: Record<AffSimAction, string> = { start: 'Start', stop: 'Stop', clear: 'Clear' };

export const AffSimulatorControls: React.FC<{ status: AffSimStatus | null; onRequested: () => Promise<void> }> = ({
  status,
  onRequested,
}) => {
  const askConfirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  if (!status) return null;

  const env = affSimEnvLabel(status.environment);
  const running = Boolean(status.startedAt);
  // One request at a time: all three wait while the collector has not acted.
  const disabled: Record<AffSimAction, boolean> = {
    start: Boolean(status.pending) || running,
    stop: Boolean(status.pending) || !running,
    clear: Boolean(status.pending) || (!running && status.simPositions === 0),
  };

  const request = async (action: AffSimAction) => {
    const confirmed = await askConfirm({
      title: `${LABEL[action]} AFF simulator`,
      message: `${LABEL[action]} the AFF simulator on ${env}? It takes effect on the collector's next run, for everyone on ${env}.`,
      confirmLabel: LABEL[action],
      destructive: action === 'clear',
    });
    if (!confirmed) return;
    setError(null);
    try {
      await requestAffSim(action);
      await onRequested();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The request failed');
    }
  };

  return (
    <div className="layer-card-sim-controls" aria-label="AFF simulator controls">
      <div className="layer-card-sim-controls__title">Simulator on {env}</div>
      <div className="layer-card-sim-controls__buttons">
        {(['start', 'stop', 'clear'] as const).map((a) => (
          <button key={a} type="button" disabled={disabled[a]} onClick={() => void request(a)}>
            {LABEL[a]}
          </button>
        ))}
      </div>
      {status.pending && <div role="status">{LABEL[status.pending]} requested, waiting for the collector</div>}
      {error && <div className="layer-card-sim-controls__error" role="alert">{error}</div>}
    </div>
  );
};
